import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import {
  checkDymoEnvironment,
  getDymoPrintersAsync,
  initDymoAsync,
  isDymoAvailable,
  printCoilLabel as servicePrintCoilLabel,
  type PrinterInfo,
  type PrintResult,
} from '../services/dymo.service';

interface DymoContextType {
  isReady: boolean;
  isAvailable: boolean;
  browserSupported: boolean;
  frameworkInstalled: boolean;
  defaultPrinter: PrinterInfo | null;
  printers: PrinterInfo[];

  /** Load the printer library and look for DYMO Connect. Safe to call more than once. */
  ensureReady: () => Promise<void>;

  /** Print a single coil label. */
  printCoilLabel: (chargeNumber: string, coilNumber: string) => PrintResult;

  /**
   * Print N coil labels in sequence. Returns one PrintResult per coil
   * in input order; continues past failures rather than aborting.
   * Awaits one tick between prints to avoid overwhelming the DYMO web
   * service.
   */
  printCoilLabels: (
    coils: { chargeNumber: string; coilNumber: string }[]
  ) => Promise<PrintResult[]>;

  refresh: () => void;
}

const DymoContext = createContext<DymoContextType | null>(null);

export function DymoProvider({ children }: { children: ReactNode }) {
  const [isReady, setIsReady] = useState(false);
  const [isAvailable, setIsAvailable] = useState(false);
  const [browserSupported, setBrowserSupported] = useState(false);
  const [frameworkInstalled, setFrameworkInstalled] = useState(false);
  const [defaultPrinter, setDefaultPrinter] = useState<PrinterInfo | null>(null);
  const [printers, setPrinters] = useState<PrinterInfo[]>([]);
  const mountedRef = useRef(true);
  const initRef = useRef<Promise<void> | null>(null);

  function updatePrinters(printerList: PrinterInfo[]) {
    setPrinters(printerList);
    const printer = printerList.find((p) => p.isConnected) || printerList[0] || null;
    setDefaultPrinter(printer);
    return printer;
  }

  const initialize = useCallback(async () => {
    const initOk = await initDymoAsync();
    if (!mountedRef.current) return;

    const available = isDymoAvailable();
    setIsAvailable(available);

    if (!initOk || !available) {
      setIsReady(true);
      return;
    }

    const env = checkDymoEnvironment();
    setBrowserSupported(env.browserSupported);
    setFrameworkInstalled(env.frameworkInstalled);

    const printerList = await getDymoPrintersAsync();
    if (!mountedRef.current) return;

    const found = updatePrinters(printerList);

    if (!found) {
      // Retry up to 5 times — the printer may take a moment to appear.
      for (let i = 0; i < 5; i++) {
        await new Promise((r) => setTimeout(r, 1000));
        if (!mountedRef.current) return;
        const retryList = await getDymoPrintersAsync();
        const retryFound = updatePrinters(retryList);
        if (retryFound) break;
      }
    }

    if (mountedRef.current) setIsReady(true);
  }, []);

  const ensureReady = useCallback(() => {
    if (!initRef.current) {
      initRef.current = initialize().catch((err) => {
        initRef.current = null;
        console.error('Failed to initialize DYMO:', err);
      });
    }
    return initRef.current;
  }, [initialize]);

  const refresh = useCallback(() => {
    initRef.current = null;
    setIsReady(false);
    void ensureReady();
  }, [ensureReady]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const printCoilLabel = useCallback((chargeNumber: string, coilNumber: string): PrintResult => {
    return servicePrintCoilLabel(chargeNumber, coilNumber);
  }, []);

  const printCoilLabels = useCallback(
    async (coils: { chargeNumber: string; coilNumber: string }[]): Promise<PrintResult[]> => {
      const results: PrintResult[] = [];
      for (const c of coils) {
        const r = servicePrintCoilLabel(c.chargeNumber, c.coilNumber);
        results.push(r);
        // Small gap between prints — the DYMO web service occasionally
        // drops jobs that arrive too close together.
        await new Promise((res) => setTimeout(res, 200));
      }
      return results;
    },
    []
  );

  const value = useMemo<DymoContextType>(
    () => ({
      isReady,
      isAvailable,
      browserSupported,
      frameworkInstalled,
      defaultPrinter,
      printers,
      ensureReady,
      printCoilLabel,
      printCoilLabels,
      refresh,
    }),
    [
      isReady,
      isAvailable,
      browserSupported,
      frameworkInstalled,
      defaultPrinter,
      printers,
      ensureReady,
      printCoilLabel,
      printCoilLabels,
      refresh,
    ]
  );

  return <DymoContext.Provider value={value}>{children}</DymoContext.Provider>;
}

export function useDymo(): DymoContextType {
  const context = useContext(DymoContext);
  if (!context) {
    throw new Error('useDymo must be used within a DymoProvider');
  }
  return context;
}
